/* @jsxImportSource @emotion/react */
import React from 'react'
import MuxPlayer from '@mux/mux-player-react/lazy'
import type {
  PortableTextBlock,
  PortableTextComponent,
  PortableTextComponents,
} from '@portabletext/react'
import { PortableText, toPlainText } from '@portabletext/react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import { ImgSanity } from './ImgSanity'
import type { InlineCtaProps } from './InlineCta'
import { InlineCta } from './InlineCta'
import type { InlineQuoteAuthor } from './InlineQuote'
import { InlineQuote } from './InlineQuote'
import { CONCATENATED_TABLE_REGEX, slugifyForSanityText } from './utils'

type ImageBlock = {
  _type: 'image'
  alt: string
  url: string
}

type VideoBlock = {
  _type: 'mux.video'
  playbackId: string
  title?: string
}

type TableRow = {
  _key: string
  _type: 'tableRow'
  cells: string[]
}

type TableBlock = {
  _type: 'table'
  rows: TableRow[]
}

type CodeBlock = {
  _type: 'code'
  code: string
  language?: string
}

type InlineCtaBlock = InlineCtaProps & {
  _type: 'inlineCta'
}

type InlineQuoteBlock = {
  _type: 'inlineQuote'
  quote: string
  size?: string | null
  author?: InlineQuoteAuthor | null
}

export type SanityTextContentProps = (
  | PortableTextBlock
  | ImageBlock
  | VideoBlock
  | TableBlock
  | CodeBlock
  | InlineCtaBlock
  | InlineQuoteBlock
)[]

type SanityTextRendererProps = {
  content: NonNullable<SanityTextContentProps>
  /** `website` draws `>` quotes as quote cards; the app keeps plain blockquotes. */
  variant?: 'default' | 'website'
}

const Image: PortableTextComponent<ImageBlock> = (img) => {
  return (
    <ImgSanity
      src={img.value.url}
      alt={img.value.alt}
      quality={75}
      css={{
        maxWidth: '100%',
        height: 'auto',
      }}
      className="radius-large margin-horizontal-auto margin-vertical-8"
    />
  )
}

const Video: PortableTextComponent<VideoBlock> = (video) => {
  return (
    <MuxPlayer
      autoPlay
      loop
      muted
      playbackId={video.value.playbackId}
      metadata={{ video_title: video.value.title || 'Video' }}
      accentColor="var(--super-pink)"
      style={{
        width: '100%',
        height: 'auto',
        borderRadius: 12,
        overflow: 'hidden',
        margin: '8px 0',
      }}
    />
  )
}

const Code: PortableTextComponent<CodeBlock> = (codeBlock) => {
  return (
    <pre
      data-language={codeBlock.value.language}
      css={{
        overflow: 'auto',
        lineHeight: 1.8,
        marginBottom: '16px',
      }}
    >
      <code>{codeBlock.value.code}</code>
    </pre>
  )
}

const Table: PortableTextComponent<TableBlock> = (table) => {
  const rows = table.value.rows
  if (!rows || rows.length === 0) {
    return null
  }

  const headerRow = rows[0]
  if (!headerRow) {
    return null
  }

  const bodyRows = rows.slice(1)

  return (
    // style is handled usually wiht .simple-rich-text table
    <table>
      <thead>
        <tr>
          {headerRow.cells.map((cell, index) => (
            <th key={index}>{cell}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {bodyRows.map((row) => (
          <tr key={row._key}>
            {row.cells.map((cell, index) => (
              <td key={index}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const InlineCtaBlockComponent: PortableTextComponent<InlineCtaBlock> = ({
  value,
}) => <InlineCta {...value} />

const InlineQuoteBlockComponent: PortableTextComponent<InlineQuoteBlock> = ({
  value,
}) => (
  <InlineQuote
    size={value.size === 'big' ? 'big' : 'small'}
    author={value.author}
  >
    {value.quote}
  </InlineQuote>
)

const blockComponents = {
  normal: ({ value, children }) => {
    const text = toPlainText(value)
    // We support markdown tables when concatened on the same line | # | Question | Super Time | Super Performance | Dust Time | Dust Performance ||---|----------|-----------|-------------------|-----------|------------------|| Q1 | What can you find about Super vs Dust? | 26s | Found the right info but buried in fluff | 21s | Gave incorrect info from Drive |...
    if (CONCATENATED_TABLE_REGEX.test(text)) {
      return (
        <Markdown remarkPlugins={[remarkGfm]}>
          {text.replace(/\|\|/g, '|\n|')}
        </Markdown>
      )
    }
    return <p>{children}</p>
  },
  h2: ({ value, children }) => {
    const slug = slugifyForSanityText(toPlainText(value))
    return <h2 id={slug}>{children}</h2>
  },
  h3: ({ value, children }) => {
    const slug = slugifyForSanityText(toPlainText(value))
    return <h3 id={slug}>{children}</h3>
  },
} satisfies PortableTextComponents['block']

const components: PortableTextComponents = {
  types: {
    image: Image,
    'mux.video': Video,
    table: Table,
    code: Code,
    inlineCta: InlineCtaBlockComponent,
    inlineQuote: InlineQuoteBlockComponent,
  },
  marks: {
    link: ({ value, children }) => (
      <a href={value?.href} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    ),
  },
  block: blockComponents,
}

const websiteComponents: PortableTextComponents = {
  ...components,
  block: {
    ...blockComponents,
    blockquote: ({ children }) => <InlineQuote>{children}</InlineQuote>,
  },
}

export function SanityTextRenderer({
  content,
  variant = 'default',
}: SanityTextRendererProps) {
  return (
    <PortableText
      value={content}
      components={variant === 'website' ? websiteComponents : components}
    />
  )
}
